import { Menu, User, LogOut } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface TopBarProps {
  onSidebarToggle: () => void;
  showToggle: boolean;
}

/**
 * TopBar component - Top bar with user avatar and logout
 * 
 * Features:
 * - Hamburger menu button (mobile/tablet only)
 * - User avatar (from user profile)
 * - User name display
 * - Logout button
 */
export const TopBar = ({ onSidebarToggle, showToggle }: TopBarProps) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  // Get user avatar URL or use default
  // Note: avatarUrl may be available from API but not in User type yet
  const avatarUrl = (user as any)?.avatarUrl || null;
  const userName = user?.name || user?.email || 'User';

  return (
    <header className="h-16 bg-background border-b border-border flex items-center justify-between px-6 sticky top-0 z-30">
      {/* Left: Hamburger menu (mobile/tablet only) */}
      <div className="flex items-center">
        {showToggle && (
          <button
            data-sidebar-toggle
            onClick={onSidebarToggle}
            className="lg:hidden p-2 rounded-md text-text-secondary hover:bg-background-secondary hover:text-text-primary transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Right: User info and logout */}
      <div className="flex items-center gap-2">
        {/* User Avatar */}
        <div className="flex items-center">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={userName}
              className="w-8 h-8 rounded-full object-cover"
              title={userName}
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center" title={userName}>
              <User className="w-5 h-5 text-primary" />
            </div>
          )}
        </div>

        {/* Logout Button - Icon only */}
        <button
          onClick={handleLogout}
          className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-md transition-colors"
          aria-label="Logout"
          title="Logout"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>
    </header>
  );
};

